import Image from 'next/image';
import Router from 'next/router';
import { useMemo } from 'react';

import { PlusIcon } from '@air/design-icons';
import {
  ActionSheet,
  ActionSheetHeader,
  type ActionSheetOptionItem,
  renderActionSheetItems,
} from '@air/primitive-action-sheet';

import { Routes } from '~/constants/routes';
import { useSwitchWorkspace } from '~/hooks/useSwitchWorkspace';
import { useWorkspaces } from '~/swr-hooks/workspaces/useWorkspaces';

const WORKSPACE_IMAGE_SIZE = 72;

export const WorkspacesActionSheet = ({ onClose }: AirModalProps) => {
  const { data: workspaces = [] } = useWorkspaces();
  const { switchWorkspace } = useSwitchWorkspace();

  const options = useMemo<ActionSheetOptionItem[]>(() => {
    const optionsArray: ActionSheetOptionItem[] = [];

    workspaces.forEach((workspace) => {
      optionsArray.push({
        id: workspace.id,
        label: workspace.name,
        prefix: (
          <Image
            alt={workspace.name}
            className="block size-6 rounded-4"
            height={WORKSPACE_IMAGE_SIZE}
            src={`${workspace.image}?h=${WORKSPACE_IMAGE_SIZE}&w=${WORKSPACE_IMAGE_SIZE}=&fit=crop`}
            width={WORKSPACE_IMAGE_SIZE}
            unoptimized
          />
        ),
        onSelect: () => {
          onClose();
          switchWorkspace(workspace);
          Router.push(Routes.home);
        },
        type: 'item',
      });
    });

    optionsArray.push({
      className: 'ads:text-accent text-blue-11',
      id: 'create-new-workspace',
      label: 'Create a new workspace',
      prefix: (
        <div className="flex size-6 items-center justify-center rounded-4 border ads:border-primary border-blue-11">
          <PlusIcon size="sm" />
        </div>
      ),
      onSelect: () => {
        Router.push(Routes.onboarding.createWorkspace);
        onClose();
      },
      type: 'item',
    });

    return optionsArray;
  }, [onClose, switchWorkspace, workspaces]);

  return (
    <ActionSheet onClose={onClose}>
      <ActionSheetHeader onClose={onClose}>Switch workspace</ActionSheetHeader>
      <div className="-m-2 flex h-full flex-col gap-3 overflow-y-auto px-2 py-3">
        {renderActionSheetItems({ options })}
      </div>
    </ActionSheet>
  );
};
